import { useCallback, useEffect, useMemo, useState } from 'react';
import { sleep } from '../util';
import { followAxiosLink, getLink } from './links';
const JOB_DELAYS_MS = [0, 200, 500, 1000, 2000, 3000, 5000, 10000];

/**
 * A hook that enables polling service endpoints that adopt the API Standards jobs pattern:
 * https://rndconfluence.sas.com/confluence/display/APISNS/Asynchronous+Execution
 * The basic pattern:
 * 1. Consumers pass in a "createJobFn" that POSTS the initial job.
 * 2. Consumers pass in a "jobHash" that uniquely identifies the job.
 * 3. The hook will handle polling the job, and will use React state to update the "job" value
 *    returned by the hook, which consumers to use to render as they see fit.
 */
export function useJob(config) {
  const [pollingJob, setPollingJob] = useState();
  const [createJobCalled, setCreateJobCalled] = useState(false);
  const pollingResultsCallback = useCallback(pollingResults => {
    setPollingJob(pollingResults);
  }, []);
  useEffect(() => {
    setPollingJob(undefined);
    setCreateJobCalled(false);
  }, [config.createJobFn, config.jobHash]);

  // Wrap the passed in createJobFn to handle some annoying stuff for the consumer
  const wrappedCreateJob = useMemo(() => {
    if (config.createJobFn && config.jobHash) {
      // Capture these current values in this closure to prevent the possibility of someone calling
      // this after they change (and to make TS happy)
      const createJobFn = config.createJobFn;
      const jobHash = config.jobHash;
      return () => {
        const abortController = new AbortController();
        setCreateJobCalled(true);
        const promise = executeJob(createJobFn, pollingResultsCallback, jobHash,
        // source.token,
        // config.disableUpdateOnCompletion
        {
          abortController,
          disableUpdateOnCompletion: config.disableUpdateOnCompletion
        });

        // React Query will call this if the query is unmounted/unused
        promise.cancel = () => {
          // TODO: Think of a better way to generalize this
          abortController.abort(
          // eslint-disable-next-line @sas/i18n/jsx-to-localize
          `Job cancelled`);
        };
        return promise;
      };
    }
  }, [config.createJobFn, config.disableUpdateOnCompletion, config.jobHash, pollingResultsCallback]);

  // Wrap a version of registerJobCallback that takes into account dependencies so consumers can just
  // call "registerJobCallback()"
  const wrappedRegisterJobCallback = useMemo(() => {
    if (config.jobHash) {
      // Capture this current value in this closure to prevent the possibility of someone calling
      // this after they change (and to make TS happy)
      const jobHash = config.jobHash;
      return () => {
        registerJobCallback(jobHash, pollingResultsCallback);
      };
    }
  }, [config.jobHash, pollingResultsCallback]);
  if (!config.deferJobCreation && !createJobCalled && config.createJobFn && config.jobHash) {
    wrappedCreateJob?.();
  }
  return {
    job: pollingJob,
    isAwaitingInitialResponse: !pollingJob && createJobCalled,
    createJob: wrappedCreateJob,
    registerJobCallback: wrappedRegisterJobCallback
  };
}

/**
 * Options for the executeJob function
 * @param options.disableUpdateOnCompletion Setting to true will prevent a final call to
 * pollingResultsCallback, which is useful for some cases like React Query usage.
 * @param options.jobDelays An array of polling delays in ms. Polling will work through each delay
 * in sequence, and continue polling at the final delay value until completion.
 * @param options.abortController An Axios AbortController, which will be honored and cause an error to throw
 * if aborted during execution. Consumers should catch this error if using this to abort.
 */

/**
 * Execute and poll a job that adopts the API Standards jobs pattern:
 * https://rndconfluence.sas.com/confluence/display/APISNS/Asynchronous+Execution
 * @param createJobFn A function defined by the consumer that creates and returns the initial job.
 * @param pollingResultsCallback A callback which will be called with updated polling results.
 * @param jobHash A string hash provided by the consumer to uniquely identify the executing job.
 * @param options Optional config values for executing the job.
 * @returns A promise that resolves to the "completed" job (completed states include: completed,
 * completedWithErrors, canceled, skipped, failed)
 */
export async function executeJob(createJobFn, pollingResultsCallback, jobHash, options) {
  try {
    // Not sure how likely it is that the job gets canceled before the create job request hits, but
    // we probably want to bail out if it does
    checkIfCancelled(options?.abortController);
    if (isJobAlreadyExecuting(jobHash)) {
      throw Error('TODO: better error message');
    }
    registerJobCallback(jobHash, pollingResultsCallback, true);
    const jobDelays = options?.jobDelays ?? JOB_DELAYS_MS;
    let polledJob;
    let completedJob;
    let jobDelayIndex = 0;
    let res;
    while (!completedJob) {
      // Bail before creating/polling if an outside source (such as React Query) has cancelled
      checkIfCancelled(options?.abortController);
      if (!polledJob) {
        // Create the initial job
        try {
          polledJob = await createJobFn(options?.abortController);
        } catch (error) {
          throw Error(`Problem creating job with hash "${jobHash}":\n${error}`);
        }
      } else {
        try {
          // Poll the job
          const selfLink = getLink('self', polledJob.links);
          if (!selfLink) {
            throw Error(`No polling link found for job with jobHash "${jobHash}"`);
          }
          res = await followAxiosLink(selfLink, {
            params: {
              wait: 0
            },
            signal: options?.abortController?.signal
          });
          polledJob = res.data;
        } catch (error) {
          throw Error(`Problem polling job with hash "${jobHash}":\n${error}`);
        }
      }

      // Bail after the request if cancelled to avoid sleep
      checkIfCancelled(options?.abortController);
      switch (polledJob.state) {
        case 'running':
        case 'pending':
        case 'canceling':
          {
            // For in-progress states that the consumer may want to act on, return polling results
            // via callbacks
            callJobCallbacks(jobHash, polledJob);
            await sleep(jobDelays[jobDelayIndex < jobDelays.length - 1 ? jobDelayIndex++ : jobDelayIndex]);
            break;
          }
        case 'completed':
        case 'completedWithErrors':
        case 'canceled':
        case 'skipped':
        case 'failed':
          {
            // All of these states mark the end of job execution, so just return the final job to the
            // consumer to handle
            completedJob = polledJob;
            if (!options?.disableUpdateOnCompletion) {
              callJobCallbacks(jobHash, completedJob);
            }
            break;
          }
      }
    }
    return completedJob;
  } finally {
    // Make sure to clear callbacks whenever this function completes
    clearCallbacksForJob(jobHash);
  }
}
const checkIfCancelled = abortController => {
  if (abortController?.signal.reason) {
    throw new Error(`Job cancelled, reason: "${abortController?.signal.reason}"`);
  }
};

// A map of all actively executing jobs, keyed by their jobHash, and the value is an array of
// callbacks
const jobCallbacksMap = new Map();

/**
 * Register a callback for a particular job hash. Typically handled by executeJob, but occasionally
 * needs to be called externally.
 * @param jobHash A string hash provided by the consumer to uniquely identify the executing job.
 * @param callback
 * @param isJobInitiator A flag to make sure the job initiator (typically executeJob) is first to
 * register a callback for a job, so that callbacks aren't registered before a job starts executing.
 */
export function registerJobCallback(jobHash, callback, isJobInitiator) {
  const registeredCallbacks = jobCallbacksMap.get(jobHash);
  const callbackRegistered = !!registeredCallbacks?.find(c => c === callback);
  if (!callbackRegistered) {
    if (registeredCallbacks) {
      registeredCallbacks.push(callback);
    } else if (isJobInitiator) {
      jobCallbacksMap.set(jobHash, [callback]);
    }
  }
}

// Check if there are registered callbacks for a request, which indicates a job is already executing
const isJobAlreadyExecuting = jobHash => jobCallbacksMap.has(jobHash);

// Call all of the callbacks for a registered on-going job
function callJobCallbacks(jobHash, job) {
  const registeredCallbacks = jobCallbacksMap.get(jobHash);
  if (registeredCallbacks) {
    for (const callback of registeredCallbacks) {
      callback(job);
    }
  }
}

// Clear a completed/failed/etc. job from the map
const clearCallbacksForJob = jobHash => {
  jobCallbacksMap.delete(jobHash);
};

// The following helpers are taken from React Query to allow us to generate stable hashes for each
// request object to uniquely identify it.

/**
 * Generate a stable string hash from any object/array.
 * @param o
 */
export const getObjectHash = o => {
  return JSON.stringify(o, (_, val) => isPlainObject(val) ? Object.keys(val).sort().reduce((result, key) => {
    result[key] = val[key];
    return result;
  }, {}) : val);
};
const isPlainObject = o => {
  if (!hasObjectPrototype(o)) {
    return false;
  }

  // If has modified constructor
  const ctor = o.constructor;
  if (typeof ctor === 'undefined') {
    return true;
  }

  // If has modified prototype
  const prot = ctor.prototype;
  if (!hasObjectPrototype(prot)) {
    return false;
  }

  // If constructor does not have an Object-specific method
  if (!prot.hasOwnProperty('isPrototypeOf')) {
    return false;
  }

  // Most likely a plain Object
  return true;
};
const hasObjectPrototype = o => {
  return Object.prototype.toString.call(o) === '[object Object]';
};
//# sourceMappingURL=jobs.js.map